// --- Our Modules --------------------------------------------------------------------------------
import config from "@/config/constants";
import type { Lang } from "@/hooks/useLang/types";
import useTranslation from "@/hooks/useTranslation";
import { orderLangs } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    value: Lang[];
    onChange: (languages: Lang[]) => void;
    disabled?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The languages a menu will be written in, picked before a store exists. Tiles rather than checkboxes
 * because this is the one choice on the create screen that shapes every form afterwards.
 *
 * Selections are kept in canonical order, never click order, and the last one cannot be unticked — a
 * menu with no language has nothing to render.
 */
export default function LanguagePicker({ value, onChange, disabled }: Props) {
    const t = useTranslation();

    const toggle = (lang: Lang) => {
        const next = value.includes(lang) ? value.filter((entry) => entry !== lang) : [...value, lang];
        if (next.length === 0) return;
        onChange(orderLangs(next));
    };

    return (
        <div className="language-picker">
            {config.menuLanguages.map((lang) => {
                const selected = value.includes(lang);
                return (
                    <button
                        key={lang}
                        type="button"
                        className="language-picker-tile"
                        aria-pressed={selected}
                        onClick={() => toggle(lang)}
                        disabled={disabled}
                    >
                        <img src={config.langFlags[lang]} className="language-picker-flag" alt="" />
                        <span className="language-picker-name">{t.languages[lang]}</span>
                        <span className="language-picker-check" aria-hidden="true">
                            {selected && <img src="/icons/check-icon.svg" alt="" width={12} height={12} />}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
